import type { StreamDiagnostics } from '../../../contracts/harness.types';
import { toolKey } from './keys';
import {
  EMPTY_TRANSCRIPT_STORE,
  type PositionalTranscriptEntry,
  type PromptContextFailureTranscriptEntry,
  type ToolTranscriptEntry,
  type TranscriptEntry,
  type TranscriptSource,
  type TranscriptStore,
} from './types';

/**
 * A mutable working copy. `commitDraft` hands back the ORIGINAL store object
 * when nothing changed — the referential stability the render memo depends on.
 */
export interface TranscriptDraft {
  byId: Record<string, TranscriptEntry>;
  keys: Record<string, string>;
  order: string[];
  toolOrder: string[];
  failureOrder: string[];
  nextOrdinal: number;
  nextStreamSlot: number;
  activeAssistantId: string | null;
  lastStreamId: string | null;
  streamDiagnostics: StreamDiagnostics | null;
  changed: boolean;
}

/** A new chat lifetime — the only whole-store clear there is. */
export const rotateTranscriptStore = (): TranscriptStore => EMPTY_TRANSCRIPT_STORE;

export const beginDraft = (store: TranscriptStore): TranscriptDraft => ({
  byId: { ...store.byId },
  keys: { ...store.keys },
  order: [...store.order],
  toolOrder: [...store.toolOrder],
  failureOrder: [...store.failureOrder],
  nextOrdinal: store.nextOrdinal,
  nextStreamSlot: store.nextStreamSlot,
  activeAssistantId: store.activeAssistantId,
  lastStreamId: store.lastStreamId,
  streamDiagnostics: store.streamDiagnostics,
  changed: false,
});

export const commitDraft = (
  store: TranscriptStore,
  draft: TranscriptDraft
): TranscriptStore =>
  draft.changed
    ? {
        byId: draft.byId,
        keys: draft.keys,
        order: draft.order,
        toolOrder: draft.toolOrder,
        failureOrder: draft.failureOrder,
        nextOrdinal: draft.nextOrdinal,
        nextStreamSlot: draft.nextStreamSlot,
        activeAssistantId: draft.activeAssistantId,
        lastStreamId: draft.lastStreamId,
        streamDiagnostics: draft.streamDiagnostics,
      }
    : store;

export const resolveEntryId = (
  draft: TranscriptDraft,
  keys: readonly string[]
): string | undefined => {
  for (const key of keys) {
    const id = draft.keys[key];
    if (id) return id;
  }
  return undefined;
};

export const bindKeys = (
  draft: TranscriptDraft,
  entryId: string,
  keys: readonly string[]
): void => {
  for (const key of keys) {
    if (draft.keys[key] === entryId) continue;
    draft.keys[key] = entryId;
    draft.changed = true;
  }
};

export const mintStreamSlot = (draft: TranscriptDraft): number => {
  const slot = draft.nextStreamSlot;
  draft.nextStreamSlot = slot + 1;
  draft.changed = true;
  return slot;
};

/**
 * Known entries keep their positions and new ones land immediately after the
 * last thing this source placed — never blindly at the tail, which would render
 * a refetched reply below a bubble submitted while it was still in flight.
 */
export interface OrderCursor {
  visit: (entryId: string) => void;
  insert: (entry: PositionalTranscriptEntry) => void;
}

export const createOrderCursor = (draft: TranscriptDraft): OrderCursor => {
  const positions = new Map(draft.order.map((id, index) => [id, index]));
  let cursor = -1;

  const reindexFrom = (from: number): void => {
    for (let index = from; index < draft.order.length; index += 1) {
      positions.set(draft.order[index]!, index);
    }
  };

  return {
    visit: (entryId) => {
      const position = positions.get(entryId);
      if (position !== undefined && position > cursor) cursor = position;
    },
    insert: (entry) => {
      const at = cursor + 1;
      draft.byId[entry.id] = entry;
      draft.order.splice(at, 0, entry.id);
      reindexFrom(at);
      cursor = at;
      draft.changed = true;
    },
  };
};

export const appendPositional = (
  draft: TranscriptDraft,
  entry: PositionalTranscriptEntry
): void => {
  draft.byId[entry.id] = entry;
  draft.order.push(entry.id);
  draft.changed = true;
};

export const mintPositionalId = (draft: TranscriptDraft): string => {
  const id = String(draft.nextOrdinal);
  draft.nextOrdinal += 1;
  draft.changed = true;
  return id;
};

export const writeEntry = (draft: TranscriptDraft, entry: TranscriptEntry): void => {
  draft.byId[entry.id] = entry;
  draft.changed = true;
};

export const readEntry = (
  draft: TranscriptDraft,
  entryId: string | undefined
): TranscriptEntry | undefined => (entryId ? draft.byId[entryId] : undefined);

/** Never positional: tool entries are reached from their `tool_call` block. */
export const ensureToolEntry = (
  draft: TranscriptDraft,
  toolCallId: string,
  source: TranscriptSource
): ToolTranscriptEntry => {
  const key = toolKey(toolCallId);
  const existingId = draft.keys[key];
  const existing = existingId ? draft.byId[existingId] : undefined;
  if (existing?.kind === 'tool') return existing;

  const entry: ToolTranscriptEntry = {
    id: `tool:${toolCallId}`,
    kind: 'tool',
    source,
    toolCallId,
  };
  draft.byId[entry.id] = entry;
  draft.keys[key] = entry.id;
  draft.toolOrder.push(entry.id);
  draft.changed = true;
  return entry;
};

export const appendFailure = (
  draft: TranscriptDraft,
  entry: PromptContextFailureTranscriptEntry,
  key: string
): void => {
  if (draft.keys[key]) return;
  draft.byId[entry.id] = entry;
  draft.keys[key] = entry.id;
  draft.failureOrder.push(entry.id);
  draft.changed = true;
};

/**
 * The one subtraction in the system, and not an ingest: the client retracting
 * an optimistic entry whose POST never reached the backend.
 */
export const withdrawEntry = (store: TranscriptStore, key: string): TranscriptStore => {
  const entryId = store.keys[key];
  if (!entryId) return store;
  const draft = beginDraft(store);
  delete draft.byId[entryId];
  for (const [candidate, id] of Object.entries(draft.keys)) {
    if (id === entryId) delete draft.keys[candidate];
  }
  draft.order = draft.order.filter((id) => id !== entryId);
  draft.changed = true;
  return commitDraft(store, draft);
};
